import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { useQueryCacheResourceHelpers } from '../../resources/utils/useQueryCacheResourceHelpers.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { useWoody } from '../../woody/woody.js';
import { useMutation } from '@tanstack/react-query';
import { resourceDetailMutationKeys } from './resourceDetailMutationKeys.js';
import { revokeUrlIfObjectUrl, getImageNaturalDimensions } from '@fabric-app/utils/files';
import { mutateResourceLinkPreview } from '../utils/mutateResourceLinkPreview.js';
import { handleUpload } from '../../upload/utils/handleUpload.js';
import { UploadTaskPriority } from '@fabric-app/types/upload';

const useMutationUpdateResourceLinkPreview = (resourceId) => {
  const { client } = useWoody();
  const { data: user } = useQueryUserInfo();
  const { updateCachedResource } = useQueryCacheResourceHelpers();
  const accessToken = useContextAccessToken();
  const mutation = useMutation({
    mutationKey: resourceDetailMutationKeys.updateResourceLinkPreview(resourceId),
    scope: {
      id: `update-resource-link-preview-${resourceId}`
    },
    mutationFn: async ({ resource, newData }) => {
      const { image, ...rest } = newData;
      let imageUploadKey;
      if (image) {
        imageUploadKey = (await handleUpload({
          data: {
            type: "file",
            file: image.file,
            available: true
          },
          userTier: user?.subscription?.tier,
          bucket: "stored-files",
          priority: UploadTaskPriority.Instant
        })).key;
      }
      const body = {
        ...rest,
        imageUploadKey
      };
      return await client.v2({
        endpoint: "/v2/resources/{resourceId}/link-preview",
        params: {
          resourceId: resource.id
        }
      }, {
        method: "patch",
        body,
        query: {
          accessToken
        }
      });
    },
    onMutate: async ({ resource, newData }) => {
      const imageDimensions = newData.image ? await getImageNaturalDimensions(newData.image.url) : null;
      return updateCachedResource({
        id: resource.id
      }, {
        resourceUpdateFn: (resource2) => mutateResourceLinkPreview(resource2, {
          description: newData.description,
          title: newData.title,
          image: newData.image ? {
            url: newData.image.url,
            width: imageDimensions?.width ?? null,
            height: imageDimensions?.height ?? null,
            mime: newData.image.file.type ?? null
          } : void 0
        })
      });
    },
    onError: (_e, _, context) => {
      if (context) {
        context.resetCacheToPreOptimisticState();
        context.invalidateQueries();
      }
    },
    onSuccess: (resource, { newData }) => {
      updateCachedResource(resource);
      if (newData.image?.url) {
        revokeUrlIfObjectUrl(newData.image.url);
      }
    }
  });
  return mutation;
};

export { useMutationUpdateResourceLinkPreview as default };
//# sourceMappingURL=useMutationUpdateResourceLinkPreview.js.map
